import type { Theme } from "../termui";
import {
  getColorAttr,
  normalizeHtmlText,
  parseCodeLanguage,
  parseIntegerAttr,
} from "../document/common";
import type { MarkdownThemeOverrides } from "../document/types";
import { type TermAction, type TermIRNode } from "./ir";

type ActionSeed =
  | { type: "action"; id?: string; text: string }
  | { type: "link"; href?: string; text: string };

export type HtmlTagCompiler = (el: Element, ctx: HtmlCompileContext) => TermIRNode | null;

export type HtmlCompileContext = {
  theme: Theme;
  overrides?: MarkdownThemeOverrides;
  compileChildren: (el: Element) => TermIRNode[];
  compileNode: (node: ChildNode) => TermIRNode | null;
  actionFromElement: (el: Element, seed: ActionSeed) => TermAction;
};

export type CompileHtmlToIROptions = {
  theme: Theme;
  overrides?: MarkdownThemeOverrides;
  containerGap?: number;
};

const htmlTagRegistry = new Map<string, HtmlTagCompiler>();

function stack(children: TermIRNode[], gap: number): TermIRNode {
  return { kind: "stack", gap, children };
}

// ---------------------------------------------------------------------------
// CSS layout inference -- read display/gap/grid from getComputedStyle
// ---------------------------------------------------------------------------

type CSSLayout = {
  display?: "flex" | "grid";
  direction?: "row" | "column";
  gap?: number;
  cols?: number;
};

function cssGapToLines(raw: string, el: Element): number | undefined {
  const match = raw.match(/^(\d+(?:\.\d+)?)\s*(px|rem|em|ch)?/);
  if (!match) return undefined;
  const value = Number.parseFloat(match[1]!);
  const unit = (match[2] ?? "px").toLowerCase();
  if (unit === "px") {
    const lh = parseFloat(getComputedStyle(el).lineHeight);
    const cellH = Number.isFinite(lh) && lh > 0 ? lh : 16;
    return Math.max(0, Math.round(value / cellH));
  }
  return Math.max(0, Math.round(value));
}

function resolveLayout(el: Element): CSSLayout {
  if (!el.isConnected || typeof getComputedStyle !== "function") return {};

  const cs = getComputedStyle(el);
  const display = cs.display;
  const isFlex = display === "flex" || display === "inline-flex";
  const isGrid = display === "grid" || display === "inline-grid";
  if (!isFlex && !isGrid) return {};

  const result: CSSLayout = { display: isFlex ? "flex" : "grid" };

  if (isFlex) {
    const dir = cs.flexDirection;
    if (dir === "row" || dir === "column") result.direction = dir;
  }

  if (isGrid) {
    const cols = cs.gridTemplateColumns;
    if (cols && cols !== "none") {
      const count = cols.split(/\s+/).filter((t) => t.length > 0).length;
      if (count > 0) result.cols = count;
    }
  }

  const gap = cs.gap;
  if (gap && gap !== "normal" && gap !== "0px") {
    result.gap = cssGapToLines(gap, el);
  }

  return result;
}

function parseNumericList(raw: string): number[] | undefined {
  const trimmed = raw.trim();
  if (!trimmed) return undefined;

  if (trimmed.startsWith("[") && trimmed.endsWith("]")) {
    try {
      const parsed = JSON.parse(trimmed);
      if (!Array.isArray(parsed)) return undefined;
      const nums = parsed
        .map((value) => (typeof value === "number" ? value : Number(value)))
        .filter((value) => Number.isFinite(value));
      return nums.length > 0 ? nums : undefined;
    } catch {
      return undefined;
    }
  }

  const nums = trimmed
    .split(/[\s,]+/g)
    .filter((token) => token.length > 0)
    .map((token) => Number.parseFloat(token))
    .filter((value) => Number.isFinite(value));
  return nums.length > 0 ? nums : undefined;
}

function parseBooleanAttr(el: Element, name: string): boolean | undefined {
  const raw = el.getAttribute(name);
  if (raw === null) return undefined;
  const value = raw.trim().toLowerCase();
  if (!value || value === "true" || value === "1" || value === "yes" || value === "on") return true;
  if (value === "false" || value === "0" || value === "no" || value === "off") return false;
  return true;
}

function firstAttr(el: Element, names: string[]): string | undefined {
  for (const name of names) {
    const value = el.getAttribute(name);
    if (value !== null) return value;
  }
  return undefined;
}

function parseIntegerAttrs(el: Element, names: string[]): number | undefined {
  for (const name of names) {
    const value = parseIntegerAttr(el, name);
    if (value !== undefined) return value;
  }
  return undefined;
}

function parseBooleanAttrs(el: Element, names: string[]): boolean | undefined {
  for (const name of names) {
    const value = parseBooleanAttr(el, name);
    if (value !== undefined) return value;
  }
  return undefined;
}

function parseNumericListAttrs(el: Element, names: string[]): number[] | undefined {
  for (const name of names) {
    const raw = el.getAttribute(name);
    if (!raw) continue;
    const parsed = parseNumericList(raw);
    if (parsed) return parsed;
    throw new Error(`Invalid numeric list in "${name}" attribute: ${raw}`);
  }
  return undefined;
}

function parseFloatAttr(el: Element, name: string): number | undefined {
  const raw = el.getAttribute(name);
  if (!raw) return undefined;
  const n = Number.parseFloat(raw.trim());
  return Number.isFinite(n) ? n : undefined;
}

function parseFloatAttrs(el: Element, names: string[]): number | undefined {
  for (const name of names) {
    const value = parseFloatAttr(el, name);
    if (value !== undefined) return value;
  }
  return undefined;
}

function parseDataAttrs(el: Element): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries((el as HTMLElement).dataset ?? {})) {
    if (typeof value === "string") out[key] = value;
  }
  return out;
}

function elementPathKey(el: Element): string {
  const parts: string[] = [];
  let current: Element | null = el;
  while (current) {
    const tag = current.tagName.toLowerCase();
    let ordinal = 0;
    let prev = current.previousElementSibling;
    while (prev) {
      if (prev.tagName.toLowerCase() === tag) ordinal += 1;
      prev = prev.previousElementSibling;
    }
    parts.push(`${tag}:${ordinal}`);
    current = current.parentElement;
  }
  return parts.reverse().join("/");
}

function actionKeyFromSeed(el: Element, seed: ActionSeed): string {
  const scope = elementPathKey(el);
  if (seed.type === "action") {
    return seed.id ? `a:${seed.id}:${scope}` : `a:${scope}`;
  }
  return seed.href ? `l:${seed.href}:${scope}` : `l:${scope}`;
}

function collectDirectChildElements(el: Element): Element[] {
  return Array.from(el.childNodes).filter((n): n is Element => n.nodeType === 1);
}

function parseDataChildren(el: Element): number[] | undefined {
  const dataEls = el.querySelectorAll(":scope > data");
  if (dataEls.length === 0) return undefined;
  const out: number[] = [];
  for (let i = 0; i < dataEls.length; i++) {
    const d = dataEls[i]!;
    const raw = d.getAttribute("value");
    const v = Number.parseFloat(raw ?? "");
    if (!Number.isFinite(v)) {
      throw new Error(`Invalid <data> value at index ${i}: ${raw ?? "(missing)"}`);
    }
    out.push(v);
  }
  return out.length > 0 ? out : undefined;
}

function controlActionId(el: Element): string | undefined {
  return firstAttr(el, ["action"]) ?? undefined;
}

function compactLabel(raw: string, max = 28): string {
  const text = normalizeHtmlText(raw);
  if (text.length <= max) return text;
  return `${text.slice(0, Math.max(1, max - 3))}...`;
}

function compileChartNode(el: Element, ctx: HtmlCompileContext): TermIRNode {
  const dataText = (el.textContent ?? "").trim();
  const data =
    parseNumericListAttrs(el, ["values"]) ??
    parseDataChildren(el) ??
    parseNumericList(dataText) ??
    [];

  return {
    kind: "chart",
    data,
    height: parseIntegerAttrs(el, ["height"]),
    fill: parseBooleanAttrs(el, ["fill"]),
    color: getColorAttr(el, ctx.theme, ["color"]),
    axisColor: getColorAttr(el, ctx.theme, ["axis-color"]),
  };
}

function compileStatusbarNode(el: Element, ctx: HtmlCompileContext): TermIRNode {
  return {
    kind: "statusbar",
    left: firstAttr(el, ["left"]) ?? "",
    right: firstAttr(el, ["right"]) ?? "",
    bg: getColorAttr(el, ctx.theme, ["bg", "background"]),
    color: getColorAttr(el, ctx.theme, ["color"]),
  };
}

function compileBarNode(el: Element, ctx: HtmlCompileContext): TermIRNode {
  const textLabel = normalizeHtmlText(el.textContent ?? "");
  return {
    kind: "bar",
    label: firstAttr(el, ["label", "aria-label", "title"]) ?? (textLabel || undefined),
    value: parseFloatAttrs(el, ["value"]) ?? 0,
    max: parseFloatAttrs(el, ["max"]) ?? 100,
    width: parseIntegerAttrs(el, ["width"]),
    color: getColorAttr(el, ctx.theme, ["color"]),
    showPercent: parseBooleanAttrs(el, ["show-percent"]),
  };
}

function registerDefaultCompilers(): void {
  if (htmlTagRegistry.size > 0) return;

  for (let level = 1; level <= 6; level++) {
    htmlTagRegistry.set(`h${level}`, (el, ctx) => {
      const text = normalizeHtmlText(el.textContent ?? "");
      if (!text) return null;
      return {
        kind: "heading",
        level,
        text,
        markerColor: getColorAttr(el, ctx.theme, ["marker-color"]),
      };
    });
  }

  htmlTagRegistry.set("p", (el, ctx) => {
    const text = normalizeHtmlText(el.textContent ?? "");
    if (!text) return null;
    return {
      kind: "paragraph",
      text,
      color:
        getColorAttr(el, ctx.theme, ["color"]) ??
        ctx.overrides?.paragraph ??
        ctx.theme.markdown.paragraph,
    };
  });

  htmlTagRegistry.set("ul", (el) => {
    const items = collectDirectChildElements(el)
      .filter((child) => child.tagName.toLowerCase() === "li")
      .map((item) => normalizeHtmlText(item.textContent ?? ""))
      .filter((text) => text.length > 0);
    if (items.length === 0) return null;
    return { kind: "list", ordered: false, start: 1, items };
  });

  htmlTagRegistry.set("ol", (el) => {
    const items = collectDirectChildElements(el)
      .filter((child) => child.tagName.toLowerCase() === "li")
      .map((item) => normalizeHtmlText(item.textContent ?? ""))
      .filter((text) => text.length > 0);
    if (items.length === 0) return null;
    return {
      kind: "list",
      ordered: true,
      start: parseIntegerAttr(el, "start") ?? 1,
      items,
    };
  });

  htmlTagRegistry.set("blockquote", (el, ctx) => {
    const text = normalizeHtmlText(el.textContent ?? "");
    if (!text) return null;
    return {
      kind: "blockquote",
      text,
      markerColor:
        getColorAttr(el, ctx.theme, ["marker-color"]) ??
        ctx.overrides?.quoteMarker ??
        ctx.theme.markdown.quoteMarker,
      color: ctx.overrides?.quoteText ?? ctx.theme.markdown.quoteText,
    };
  });

  htmlTagRegistry.set("hr", (el, ctx) => {
    const label = firstAttr(el, ["label"]);
    if (label) {
      return {
        kind: "divider",
        label,
        color: getColorAttr(el, ctx.theme, ["color"]) ?? ctx.theme.semantic.border,
        char: firstAttr(el, ["char"]) ?? undefined,
        labelColor: getColorAttr(el, ctx.theme, ["label-color"]) ?? undefined,
      };
    }
    return {
      kind: "separator",
      color:
        getColorAttr(el, ctx.theme, ["color"]) ??
        ctx.overrides?.separator ??
        ctx.theme.markdown.separator,
    };
  });

  htmlTagRegistry.set("pre", (el, ctx) => {
    const language = parseCodeLanguage(el) ?? parseCodeLanguage(el.querySelector("code") ?? el);
    const raw = (el.textContent ?? "").replace(/\r\n/g, "\n");
    const trimmed = raw.replace(/^\n+|\n+$/g, "");

    const lines = trimmed.length > 0 ? trimmed.split("\n").map((line) => line.replace(/\t/g, "  ")) : [];
    return {
      kind: "pre",
      lines,
      language,
      textColor:
        getColorAttr(el, ctx.theme, ["text-color", "color"]) ??
        ctx.overrides?.codeText ??
        ctx.theme.markdown.codeText,
      borderColor:
        getColorAttr(el, ctx.theme, ["border-color"]) ??
        ctx.overrides?.codeBorder ??
        ctx.theme.markdown.codeBorder,
      emptyColor: ctx.overrides?.codeEmpty ?? ctx.theme.markdown.codeEmpty,
    };
  });

  htmlTagRegistry.set("table", (el, ctx) => {
    const rowElements: Element[] = [];
    for (const child of collectDirectChildElements(el)) {
      const tag = child.tagName.toLowerCase();
      if (tag === "tr") {
        rowElements.push(child);
        continue;
      }
      if (tag !== "thead" && tag !== "tbody" && tag !== "tfoot") continue;
      for (const row of collectDirectChildElements(child)) {
        if (row.tagName.toLowerCase() === "tr") rowElements.push(row);
      }
    }

    const rows = rowElements
      .map((rowEl) => {
        const inHead = rowEl.parentElement?.tagName.toLowerCase() === "thead";
        return collectDirectChildElements(rowEl)
          .filter((cell) => {
            const tag = cell.tagName.toLowerCase();
            return tag === "th" || tag === "td";
          })
          .map((cell) => ({
            text: normalizeHtmlText(cell.textContent ?? ""),
            header: inHead || cell.tagName.toLowerCase() === "th",
          }));
      })
      .filter((cells) => cells.length > 0);

    if (rows.length === 0) {
      return {
        kind: "paragraph",
        text: "(empty table)",
        color: ctx.theme.semantic.frameMuted,
      };
    }

    const first = rows[0]!;
    const hasHeader = first.some((cell) => cell.header);
    const headers = hasHeader
      ? first.map((cell, index) => cell.text || `col_${index + 1}`)
      : first.map((_, index) => `col_${index + 1}`);
    const bodyRows = rows
      .slice(hasHeader ? 1 : 0)
      .map((row) => headers.map((_, index) => row[index]?.text ?? ""));

    return {
      kind: "table",
      headers,
      rows: bodyRows,
      headerColor:
        getColorAttr(el, ctx.theme, ["header-color"]) ??
        ctx.overrides?.tableHeader ??
        ctx.theme.markdown.tableHeader,
      borderColor:
        getColorAttr(el, ctx.theme, ["border-color"]) ??
        ctx.overrides?.tableBorder ??
        ctx.theme.markdown.tableBorder,
    };
  });

  htmlTagRegistry.set("button", (el, ctx) => {
    const label = normalizeHtmlText(el.textContent ?? el.getAttribute("label") ?? "");
    if (!label) return null;
    const id = controlActionId(el);
    return {
      kind: "button",
      label,
      color: getColorAttr(el, ctx.theme, ["color"]),
      bg: getColorAttr(el, ctx.theme, ["bg", "background"]),
      action: ctx.actionFromElement(el, { type: "action", id, text: label }),
    };
  });

  htmlTagRegistry.set("summary", (el, ctx) => {
    const parent = el.parentElement;
    const open = parent instanceof HTMLDetailsElement ? parent.open : false;
    const id = controlActionId(parent ?? el);
    const label = compactLabel(normalizeHtmlText(el.textContent ?? "") || "details");
    const text = `${open ? "[-]" : "[+]"} ${label}`;
    return {
      kind: "button",
      label: text,
      action: ctx.actionFromElement(el, { type: "action", id, text }),
    };
  });

  htmlTagRegistry.set("details", (el, ctx) => {
    const gap = parseIntegerAttrs(el, ["gap"]) ?? 1;
    const children = ctx.compileChildren(el);
    if ((el as HTMLDetailsElement).open) {
      return stack(children, gap);
    }
    const summary = children[0];
    return stack(summary ? [summary] : [], gap);
  });

  htmlTagRegistry.set("input", (el, ctx) => {
    const input = el as HTMLInputElement;
    const type = (input.type || "text").toLowerCase();
    const id = controlActionId(el);
    const color = getColorAttr(el, ctx.theme, ["color"]);
    const bg = getColorAttr(el, ctx.theme, ["bg", "background"]);

    if (type === "hidden") return null;

    if (type === "checkbox") {
      const fallback = input.name || input.id || "checkbox";
      const label = compactLabel(firstAttr(el, ["label", "aria-label"]) ?? fallback);
      const state = input.checked ? "[x]" : "[ ]";
      const text = label ? `${state} ${label}` : state;
      return {
        kind: "button",
        label: text,
        color,
        bg,
        action: ctx.actionFromElement(el, { type: "action", id, text }),
      };
    }

    if (type === "radio") {
      const fallback = input.name || input.id || "radio";
      const label = compactLabel(firstAttr(el, ["label", "aria-label"]) ?? fallback);
      const state = input.checked ? "(*)" : "( )";
      const text = label ? `${state} ${label}` : state;
      return {
        kind: "button",
        label: text,
        color,
        bg,
        action: ctx.actionFromElement(el, { type: "action", id, text }),
      };
    }

    if (type === "button" || type === "submit" || type === "reset") {
      const label = compactLabel(
        normalizeHtmlText(input.value || firstAttr(el, ["label", "aria-label"]) || type),
      );
      return {
        kind: "button",
        label,
        color,
        bg,
        action: ctx.actionFromElement(el, { type: "action", id, text: label }),
      };
    }

    const value = compactLabel(input.value || input.placeholder || input.name || input.id || type);
    const text = type === "file" ? `[file] ${value}` : `[input] ${value}`;
    return {
      kind: "button",
      label: text,
      color,
      bg,
      action: ctx.actionFromElement(el, { type: "action", id, text }),
    };
  });

  htmlTagRegistry.set("textarea", (el, ctx) => {
    const area = el as HTMLTextAreaElement;
    const id = controlActionId(el);
    const color = getColorAttr(el, ctx.theme, ["color"]);
    const bg = getColorAttr(el, ctx.theme, ["bg", "background"]);
    const value = compactLabel(area.value || area.placeholder || area.name || area.id || "textarea");
    const text = `[textarea] ${value}`;
    return {
      kind: "button",
      label: text,
      color,
      bg,
      action: ctx.actionFromElement(el, { type: "action", id, text }),
    };
  });

  htmlTagRegistry.set("select", (el, ctx) => {
    const select = el as HTMLSelectElement;
    const id = controlActionId(el);
    const color = getColorAttr(el, ctx.theme, ["color"]);
    const bg = getColorAttr(el, ctx.theme, ["bg", "background"]);
    const selected = select.selectedOptions[0] ?? select.options[select.selectedIndex] ?? select.options[0];
    const selectedText = compactLabel(selected?.textContent ?? "");
    const label = compactLabel(select.name || select.id || "select");
    const text = selectedText ? `${label}: ${selectedText}` : `${label}: (none)`;
    return {
      kind: "button",
      label: text,
      color,
      bg,
      action: ctx.actionFromElement(el, { type: "action", id, text }),
    };
  });

  htmlTagRegistry.set("img", (el) => {
    const src = el.getAttribute("src")?.trim();
    if (!src) return null;
    return {
      kind: "media" as const,
      tag: "img",
      src,
      alt: el.getAttribute("alt")?.trim() ?? undefined,
      widthCells: parseIntegerAttrs(el, ["width"]) ?? undefined,
      heightLines: parseIntegerAttrs(el, ["height"]) ?? undefined,
    };
  });

  htmlTagRegistry.set("video", (el) => {
    const src = el.getAttribute("src")?.trim() ?? undefined;
    const attrs: Record<string, string> = {};
    if (el.hasAttribute("controls")) attrs.controls = "";
    if (el.hasAttribute("autoplay")) attrs.autoplay = "";
    if (el.hasAttribute("loop")) attrs.loop = "";
    if (el.hasAttribute("muted")) attrs.muted = "";
    const poster = el.getAttribute("poster")?.trim();
    if (poster) attrs.poster = poster;
    return {
      kind: "media" as const,
      tag: "video",
      src,
      widthCells: parseIntegerAttrs(el, ["width"]) ?? undefined,
      heightLines: parseIntegerAttrs(el, ["height"]) ?? undefined,
      attrs: Object.keys(attrs).length > 0 ? attrs : undefined,
    };
  });

  htmlTagRegistry.set("audio", (el) => {
    const src = el.getAttribute("src")?.trim() ?? undefined;
    const attrs: Record<string, string> = {};
    if (el.hasAttribute("controls")) attrs.controls = "";
    if (el.hasAttribute("autoplay")) attrs.autoplay = "";
    if (el.hasAttribute("loop")) attrs.loop = "";
    return {
      kind: "media" as const,
      tag: "audio",
      src,
      attrs: Object.keys(attrs).length > 0 ? attrs : undefined,
    };
  });

  htmlTagRegistry.set("progress", (el, ctx) => compileBarNode(el, ctx));

  htmlTagRegistry.set("a", (el, ctx) => {
    const text = normalizeHtmlText(el.textContent ?? "");
    if (!text) return null;
    const href = el.getAttribute("href")?.trim();
    return {
      kind: "link",
      text,
      color: getColorAttr(el, ctx.theme, ["color"]) ?? ctx.theme.semantic.info,
      action: ctx.actionFromElement(el, { type: "link", href: href || undefined, text }),
    };
  });

  // -- Smart semantic inference --
  // <article title="..."> → box
  htmlTagRegistry.set("article", (el, ctx) => {
    const title = el.getAttribute("title") ?? undefined;
    if (title) {
      return {
        kind: "box",
        title,
        borderColor:
          getColorAttr(el, ctx.theme, ["border-color"]) ??
          ctx.theme.semantic.border,
        gap: parseIntegerAttrs(el, ["gap"]) ?? 0,
        children: ctx.compileChildren(el),
      };
    }
    return stack(ctx.compileChildren(el), parseIntegerAttrs(el, ["gap"]) ?? 1);
  });

  // <figure values="..."> → chart; otherwise container
  htmlTagRegistry.set("figure", (el, ctx) => {
    const data = parseNumericListAttrs(el, ["values"]);
    if (data) return compileChartNode(el, ctx);
    return stack(ctx.compileChildren(el), parseIntegerAttrs(el, ["gap"]) ?? 0);
  });

  // <footer left="..." right="..."> → statusbar; otherwise container
  htmlTagRegistry.set("footer", (el, ctx) => {
    const left = firstAttr(el, ["left"]);
    const right = firstAttr(el, ["right"]);
    if (left !== undefined || right !== undefined) return compileStatusbarNode(el, ctx);
    return stack(ctx.compileChildren(el), parseIntegerAttrs(el, ["gap"]) ?? 1);
  });

  // <nav> → hstack (navigation is inherently horizontal)
  htmlTagRegistry.set("nav", (el, ctx) => ({
    kind: "hstack",
    gap: parseIntegerAttrs(el, ["gap"]) ?? 1,
    widths: parseNumericListAttrs(el, ["widths"]),
    children: ctx.compileChildren(el),
  }));
}

function createCompileContext(options: CompileHtmlToIROptions): {
  compileElement: (el: Element) => TermIRNode | null;
} {
  const containerGap = options.containerGap ?? 1;
  let ctx!: HtmlCompileContext;

  const compileNode = (node: ChildNode): TermIRNode | null => {
    if (node.nodeType === 3) {
      const text = normalizeHtmlText(node.textContent ?? "");
      if (!text) return null;
      return {
        kind: "paragraph",
        text,
        color: options.overrides?.paragraph ?? options.theme.markdown.paragraph,
      };
    }
    if (node.nodeType !== 1) return null;
    return compileElement(node as Element);
  };

  const compileChildren = (el: Element): TermIRNode[] => {
    const out: TermIRNode[] = [];
    for (const node of Array.from(el.childNodes)) {
      const next = compileNode(node);
      if (next) out.push(next);
    }
    return out;
  };

  const actionFromElement = (el: Element, seed: ActionSeed): TermAction => ({
    ...seed,
    key: actionKeyFromSeed(el, seed),
    tag: el.tagName.toLowerCase(),
    dataset: parseDataAttrs(el),
    sourceElement: el,
  });

  const compileElement = (el: Element): TermIRNode | null => {
    const tag = el.tagName.toLowerCase();
    const compiler = htmlTagRegistry.get(tag);
    if (compiler) return compiler(el, ctx);

    const layout = resolveLayout(el);
    const cols = parseIntegerAttrs(el, ["cols"]) ?? layout.cols;
    const gap = parseIntegerAttrs(el, ["gap"]) ?? layout.gap;
    const children = compileChildren(el);
    const effectiveChildren = (() => {
      if (children.length > 0) return children;
      if (!(el instanceof HTMLElement)) return children;
      if (!el.shadowRoot || el.shadowRoot.mode !== "open") return children;
      const shadowChildren: TermIRNode[] = [];
      for (const node of Array.from(el.shadowRoot.childNodes)) {
        const next = compileNode(node);
        if (next) shadowChildren.push(next);
      }
      return shadowChildren.length > 0 ? shadowChildren : children;
    })();

    if (cols || layout.display === "grid") {
      return {
        kind: "grid",
        cols: cols ?? 2,
        gap: gap ?? 1,
        rowGap: parseIntegerAttrs(el, ["row-gap"]) ?? 0,
        children: effectiveChildren,
      };
    }

    if (layout.display === "flex" && layout.direction !== "column") {
      return {
        kind: "hstack",
        gap: gap ?? 1,
        widths: parseNumericListAttrs(el, ["widths"]),
        children: effectiveChildren,
      };
    }

    return stack(effectiveChildren, gap ?? containerGap);
  };

  ctx = {
    theme: options.theme,
    overrides: options.overrides,
    compileChildren,
    compileNode,
    actionFromElement,
  };

  return { compileElement };
}

registerDefaultCompilers();

export function registerHtmlTagCompiler(tag: string, compiler: HtmlTagCompiler): void {
  registerDefaultCompilers();
  htmlTagRegistry.set(tag.trim().toLowerCase(), compiler);
}

export function compileHtmlElementToIR(root: Element, options: CompileHtmlToIROptions): TermIRNode {
  registerDefaultCompilers();
  const { compileElement } = createCompileContext(options);
  return compileElement(root) ?? stack([], options.containerGap ?? 1);
}

export function compileHtmlToIR(html: string, options: CompileHtmlToIROptions): TermIRNode {
  if (typeof DOMParser === "undefined") {
    throw new Error("DOMParser is unavailable; HTML compilation requires a DOM environment.");
  }
  const doc = new DOMParser().parseFromString(html, "text/html");
  return compileHtmlElementToIR(doc.body, options);
}
